/**
 * ErrorFallback Component
 *
 * User-friendly error UI that displays when an error is caught by ErrorBoundary.
 * Styled to match Material Dashboard theme.
 */

import { FC } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Collapse from "@mui/material/Collapse";
import { useState } from "react";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 PRO React TS contexts
import { useMaterialUIController } from "context";

// Types
import { ErrorFallbackProps } from "./types";

const ErrorFallback: FC<ErrorFallbackProps> = ({
  error,
  errorInfo,
  resetError,
  showDetails = process.env.NODE_ENV === 'development',
  level = 'feature',
  featureName,
}) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const isProduction = process.env.NODE_ENV === 'production';

  // Determine error severity and messaging
  const getErrorConfig = (): {
    icon: string;
    title: string;
    message: string;
    bgColor: 'error' | 'warning' | 'info';
  } => {
    switch (level) {
      case 'app':
        return {
          icon: 'error',
          title: 'Application Error',
          message: isProduction
            ? 'We encountered an unexpected error. Please refresh the page to continue.'
            : 'A critical error occurred in the application.',
          bgColor: 'error' as const,
        };
      case 'route':
        return {
          icon: 'warning',
          title: 'Page Error',
          message: isProduction
            ? 'This page encountered an error. Please try navigating back or refreshing.'
            : 'An error occurred while loading this page.',
          bgColor: 'warning' as const,
        };
      case 'feature':
        return {
          icon: 'info',
          title: featureName ? `${featureName} Error` : 'Feature Error',
          message: isProduction
            ? 'This feature is temporarily unavailable. Please try again.'
            : `An error occurred in the ${featureName || 'feature'}.`,
          bgColor: 'info' as const,
        };
      default:
        return {
          icon: 'error',
          title: 'Error',
          message: 'An unexpected error occurred.',
          bgColor: 'error' as const,
        };
    }
  };

  const config = getErrorConfig();

  const handleReload = () => {
    if (level === 'app') {
      window.location.reload();
    } else {
      resetError();
    }
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight={level === 'app' ? '100vh' : '400px'}
      p={3}
      bgColor={darkMode ? 'transparent' : 'grey-100'}
    >
      <Card
        sx={{
          maxWidth: 600,
          width: '100%',
          overflow: 'visible',
        }}
      >
        <MDBox p={3}>
          {/* Error Icon */}
          <MDBox display="flex" justifyContent="center" mb={2}>
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="4rem"
              height="4rem"
              borderRadius="50%"
              bgColor={config.bgColor}
              color="white"
              shadow="md"
            >
              <Icon fontSize="large">{config.icon}</Icon>
            </MDBox>
          </MDBox>

          {/* Error Title */}
          <MDTypography
            variant="h4"
            fontWeight="bold"
            textAlign="center"
            mb={1}
            color={darkMode ? 'white' : 'dark'}
          >
            {config.title}
          </MDTypography>

          {/* Error Message */}
          <MDTypography
            variant="body2"
            textAlign="center"
            color="text"
            mb={3}
          >
            {config.message}
          </MDTypography>

          {/* Error Details (Development Only or when showDetails is true) */}
          {showDetails && error && (
            <MDBox mb={3}>
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => setDetailsExpanded(!detailsExpanded)}
                sx={{ cursor: 'pointer' }}
                p={1}
                borderRadius="md"
                bgColor={darkMode ? 'grey-800' : 'grey-100'}
              >
                <MDTypography variant="button" fontWeight="medium" color="text">
                  Error Details
                </MDTypography>
                <Icon>{detailsExpanded ? 'expand_less' : 'expand_more'}</Icon>
              </MDBox>

              <Collapse in={detailsExpanded}>
                <MDBox
                  mt={1}
                  p={2}
                  borderRadius="md"
                  bgColor={darkMode ? 'grey-900' : 'grey-50'}
                  sx={{
                    maxHeight: 300,
                    overflowY: 'auto',
                    fontFamily: 'monospace',
                  }}
                >
                  <MDTypography variant="caption" color="text" component="div">
                    <strong>Error:</strong> {error.name}
                  </MDTypography>
                  <MDTypography variant="caption" color="text" component="div" mb={1}>
                    <strong>Message:</strong> {error.message}
                  </MDTypography>
                  {error.stack && (
                    <MDTypography variant="caption" color="text" component="div" mb={1}>
                      <strong>Stack Trace:</strong>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                        {error.stack}
                      </pre>
                    </MDTypography>
                  )}
                  {errorInfo?.componentStack && (
                    <MDTypography variant="caption" color="text" component="div">
                      <strong>Component Stack:</strong>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                        {errorInfo.componentStack}
                      </pre>
                    </MDTypography>
                  )}
                </MDBox>
              </Collapse>
            </MDBox>
          )}

          {/* Action Buttons */}
          <MDBox display="flex" gap={2} justifyContent="center" flexWrap="wrap">
            <MDButton
              variant="gradient"
              color={config.bgColor}
              onClick={handleReload}
              startIcon={<Icon>refresh</Icon>}
            >
              {level === 'app' ? 'Reload Page' : 'Try Again'}
            </MDButton>

            {level !== 'app' && (
              <MDButton
                variant="outlined"
                color="secondary"
                onClick={handleGoBack}
                startIcon={<Icon>arrow_back</Icon>}
              >
                Go Back
              </MDButton>
            )}
          </MDBox>

          {/* Help Text */}
          <MDBox mt={3} textAlign="center">
            <MDTypography variant="caption" color="text">
              If this problem persists, please contact support.
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    </MDBox>
  );
};

export default ErrorFallback;
